import React, { useState, useEffect } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { getGovernanceOverlays } from "../../../workflow/logic";

export const GovernanceBadge: React.FC = () => {
  const { state, selectedOpportunity } = useMonyawn();
  const [isSweeping, setIsSweeping] = useState(false);
  const [sweepProgress, setSweepProgress] = useState(0);

  useEffect(() => {
    if (isSweeping) {
      const timer = setInterval(() => {
        setSweepProgress((prev) => {
          if (prev >= 100) {
            clearInterval(timer);
            setIsSweeping(false);
            return 100;
          }
          return prev + 5;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [isSweeping]);

  const account = state.accounts.find((a) => a.account_id === state.selectedAccountId);
  const user = state.users.find((u) => u.user_id === state.selectedUserId);
  const opportunity = selectedOpportunity;

  if (!account || !user || !opportunity) return null;

  const overlays = getGovernanceOverlays(account, user, opportunity);

  const startSweep = () => {
    setIsSweeping(true);
    setSweepProgress(0);
  };

  if (overlays.length === 0) {
    return (
      <div className="fixed bottom-20 md:bottom-6 right-6 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
          CTO Verified
        </span>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 md:bottom-6 right-6 z-50 flex flex-col items-end gap-2 max-w-[calc(100vw-3rem)] sm:max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
      {overlays.map((overlay, i) => (
        <div
          key={i}
          className={`flex items-start gap-3 px-4 py-3 rounded-2xl bg-white border shadow-xl transition-all duration-500 ${
            overlay.severity === "block" ? "border-red-200" : "border-amber-100"
          } ${isSweeping && sweepProgress < (i + 1) * (100 / overlays.length) ? "opacity-50 blur-[1px]" : "opacity-100"}`}
        >
          <div
            className={`flex-none w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
              overlay.severity === "block" ? "bg-red-500 text-white" : "bg-amber-500 text-white"
            }`}
          >
            <span className="text-[10px] font-black">
              {overlay.severity === "block" ? "!" : "!"}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <p
              className={`text-[10px] font-bold uppercase tracking-tight ${
                overlay.severity === "block" ? "text-red-700" : "text-amber-700"
              }`}
            >
              {overlay.expert}
            </p>
            <p className="text-[11px] font-medium text-brand-ink leading-tight">{overlay.text}</p>
          </div>
        </div>
      ))}

      <button
        onClick={startSweep}
        disabled={isSweeping}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-80"
      >
        <div
          className={`w-1.5 h-1.5 rounded-full ${isSweeping ? "bg-brand-accent animate-ping" : "bg-amber-400 animate-pulse"}`}
        />
        <span className="text-[10px] font-bold uppercase tracking-widest">
          {isSweeping ? `Technical Sweep ${sweepProgress}%` : "Request CTO Review"}
        </span>
      </button>
    </div>
  );
};
